import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import { AxiosError } from 'axios';
import {
  Avatar,
  Colors,
  Icon,
  IconName,
  SizesAvatar,
  Text,
  Typography,
} from 'goalsetter-webapp-components';
import { useMediaQueryGs } from 'app/hooks/useMediaQuery';
import useFetchData from 'app/hooks/useFetchData';
import {
  Container,
  GoalCardBackground,
  GoalsContainer,
  IconWrapper,
  StyledCard,
  StyledInfoContainer,
  StyledProfileInfoContainer,
  buttonStyle,
  goalCardDetailContainerStyle,
  subtitleStyle,
} from './styles';
import { useNavigate } from 'react-router-dom';
import { ActiveGoalsEntity, FamilyMember } from 'types/goals';
import { RoutesGs } from 'router/RoutesList';
import { useParams } from 'react-router-dom';
import { userService } from 'api';
import { Trans } from 'react-i18next';
import { Button, Flex } from '@mantine/core';
import GoalCardDetails from 'app/components/GoalCardDetails';
import { CSSProperties } from 'styled-components';
import { paddingLeft } from '../sendGoalcard/SendGift/styles';
import LogoHeader from 'app/components/LogoHeader';
import { REACT_APP_GIVEGOALCARDS } from 'utils/environment';
import SimpleModal from 'app/components/Modals/SimpleModal';

export const Profile: React.FC = () => {
  const { isMobile } = useMediaQueryGs();
  const { userName, goalName } = useParams();
  const [selectedGoal, setSelectedGoal] = useState<ActiveGoalsEntity | null>(
    null,
  );
  const [showNotAllowedModal, setShowNotAllowedModal] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const { data, loading, fetchData } = useFetchData<
    FamilyMember[],
    AxiosError
  >();

  const {
    data: publicGoals,
    loading: loadingPublicGoals,
    fetchData: fetchPublicGoals,
  } = useFetchData<ActiveGoalsEntity[], AxiosError>();

  useEffect(() => {
    if (!goalName) {
      setSelectedGoal(null);
      return;
    }
    const goalExists = publicGoals?.filter(
      (a: ActiveGoalsEntity) => a.alias === goalName,
    );
    if (goalExists?.length === 1) {
      setSelectedGoal(goalExists[0]);
    }
    if (publicGoals && !loadingPublicGoals && !goalExists?.length && userName) {
      navigate(RoutesGs.UserGoalId.replace(':userName/:goalName', userName));
    }
  }, [publicGoals, goalName, loadingPublicGoals, userName, navigate]);

  const userFound = data && (data[0] as FamilyMember);
  const hasNoGoals =
    userFound &&
    !loadingPublicGoals &&
    publicGoals &&
    publicGoals?.length === 0;

  const userFirstName = (userFound && userFound.firstname) || '';
  const userLastName = (userFound && userFound.lastName) || '';
  const userProfileImageUrl = (userFound && userFound.profileImageUrl) || '';
  const userProfileFullName = (userFound && userFound.fullName) || '';

  const showGoals = Boolean(
    publicGoals && publicGoals.length > 0 && !loadingPublicGoals,
  );

  const goToSelectGoal = (selectedGoal: ActiveGoalsEntity) =>
    navigate(
      RoutesGs.UserGoalId.replace(
        ':userName/:goalName',
        `${userName}/${selectedGoal.alias}` || '',
      ),
    );

  const goToSelectCard = () => {
    if (userFound && !userFound.canReceiveMoney) {
      return setShowNotAllowedModal(true);
    }
    window.location.replace(String(REACT_APP_GIVEGOALCARDS));
  };

  const goToSendGift = () =>
    navigate(RoutesGs.SendGift, {
      state: {
        userNotFound: true,
      },
    });

  useEffect(() => {
    if (!userName) return;
    fetchData(userService.getPublicInformationByUsername(userName));
  }, []);

  useEffect(() => {
    if (data && !data.length && !loading) {
      // User doesn not exist, redirect to /gift
      goToSendGift();
    }
    if (data && !loading) {
      fetchPublicGoals(userService.getPublicGoals(data[0]?.rid));
    }
  }, [data]);

  const handleCloseNotAllowedModal = () => setShowNotAllowedModal(false);

  return (
    <GoalCardBackground>
      <Container>
        <LogoHeader />
        {hasNoGoals && (
          <StyledCard>
            <IconWrapper>
              <Icon
                icon={IconName.ATTENTION}
                size={48}
                color={Colors.WHITE}
              />
            </IconWrapper>
            <Flex
              direction="column"
              style={paddingLeft}
            >
              <Text
                color={Colors.WHITE}
                type={Typography.GSBODY3}
              >
                {t('profile.goalNotFound.title')}
              </Text>
              <Text
                color={Colors.WHITE}
                type={Typography.GSCAPTION}
              >
                {t('profile.goalNotFound.description')}
              </Text>
            </Flex>
          </StyledCard>
        )}
        <StyledProfileInfoContainer
          isLoading={loading || !!(data && !data.length)}
          hasNoGoals={Boolean(hasNoGoals)}
        >
          {selectedGoal ? (
            <div>
              <Text
                type={isMobile ? Typography.GSH4 : Typography.GSH1}
                color={Colors.WHITE}
              >
                {userFound?.firstname}
              </Text>{' '}
              <Text
                type={isMobile ? Typography.GSH4 : Typography.GSH2}
                color={Colors.WHITE}
              >
                {t('profile.profileDetailCard.isSavingFor')}
              </Text>
            </div>
          ) : (
            <>
              <Avatar
                user={
                  userProfileImageUrl
                    ? { profileImageUrl: userProfileImageUrl }
                    : {
                        firstName: userFirstName,
                        lastName: userLastName,
                      }
                }
                size={isMobile ? SizesAvatar.LARGE : SizesAvatar.EXTRA_LARGE}
              />
              <StyledInfoContainer>
                <Text
                  type={Typography.GSSUBTITLE1}
                  color={Colors.WHITE}
                >
                  {t('profile.title')}
                </Text>
                <Text
                  type={isMobile ? Typography.GSH4 : Typography.GSH3}
                  color={Colors.WHITE}
                  style={subtitleStyle(isMobile) as CSSProperties}
                >
                  {`${userProfileFullName}`}
                </Text>

                <Button
                  onClick={goToSelectCard}
                  style={buttonStyle(isMobile)}
                >
                  <Trans
                    i18nKey={t('profile.buttonLabel')}
                    values={{ reg: '®' }}
                    components={{
                      span: (
                        <Text
                          type={Typography.GSOVERLINE}
                          color={Colors.WHITE}
                        >
                          {''}
                        </Text>
                      ),
                      parent: (
                        <Text
                          type={Typography.GSBUTTON}
                          color={Colors.WHITE}
                        >
                          {''}
                        </Text>
                      ),
                    }}
                  />
                </Button>
              </StyledInfoContainer>
            </>
          )}
        </StyledProfileInfoContainer>
        {selectedGoal && (
          <Flex style={goalCardDetailContainerStyle}>
            <GoalCardDetails
              goal={selectedGoal}
              selection={goToSelectGoal}
              singleCard
              goToSelectCard={goToSelectCard}
            />
          </Flex>
        )}
        <GoalsContainer
          length={Number(publicGoals?.length) < 3 ? publicGoals?.length : 3}
        >
          {showGoals &&
            !selectedGoal &&
            publicGoals?.map((goal: ActiveGoalsEntity, index: number) => {
              return (
                <GoalCardDetails
                  key={index}
                  goal={goal}
                  selection={goToSelectGoal}
                  goToSelectCard={goToSelectCard}
                />
              );
            })}
        </GoalsContainer>
      </Container>
      <SimpleModal
        show={showNotAllowedModal}
        onClose={handleCloseNotAllowedModal}
        title={String(t('userNotAbleToReceiveMoneyModal.title'))}
        description={String(t('userNotAbleToReceiveMoneyModal.subtitle'))}
      />
    </GoalCardBackground>
  );
};
